import { z } from 'zod'

/**
 * Provenance d'une demande (module Google, tâche 5, migration `20261126010000_lead_attribution_v1`). L'origine
 * technique (site, tablette, saisie) est séparée de la provenance marketing. Une source déclarée « Google » ne devient
 * jamais « Google Ads ».
 */
export const leadDeclaredSources = [
  'GOOGLE',
  'RECOMMENDATION',
  'FACEBOOK',
  'WAITING_ROOM',
  'OTHER',
  'UNKNOWN',
] as const
export type LeadDeclaredSource = (typeof leadDeclaredSources)[number]

export const leadDeclaredSourceLabels: Record<LeadDeclaredSource, string> = {
  GOOGLE: 'Google (recherche, Maps ou annonce)',
  RECOMMENDATION: 'Recommandation',
  FACEBOOK: 'Facebook',
  WAITING_ROOM: 'Salle d’attente',
  OTHER: 'Autre',
  UNKNOWN: 'Ne sait pas',
}

export const leadAttributionChannelLabels = {
  GOOGLE_ADS: 'Google Ads',
  GOOGLE_ORGANIC: 'Recherche Google',
  GOOGLE_BUSINESS: 'Fiche Google',
  SOCIAL: 'Réseaux sociaux',
  EMAIL: 'Courriel',
  REFERRAL: 'Site référent',
  OTHER_CAMPAIGN: 'Autre campagne',
  DIRECT: 'Accès direct',
  UNKNOWN: 'Inconnue',
} as const
export type LeadAttributionChannel = keyof typeof leadAttributionChannelLabels

export const leadFormOriginLabels = {
  WEBSITE_FORM: 'Formulaire du site',
  WAITING_ROOM: 'Tablette « Salle d’attente »',
  MANUAL: 'Saisie manuelle',
  UNKNOWN: 'Inconnue',
} as const

export const leadAttributionQualityLabels = {
  TRACKED: 'Suivie',
  DECLARED: 'Déclarée',
  UNKNOWN: 'Inconnue',
} as const

export const leadAttributionSchema = z.object({
  formOrigin: z.enum(['WEBSITE_FORM', 'WAITING_ROOM', 'MANUAL', 'UNKNOWN']),
  channel: z.enum(
    Object.keys(leadAttributionChannelLabels) as [LeadAttributionChannel, ...LeadAttributionChannel[]],
  ),
  quality: z.enum(['TRACKED', 'DECLARED', 'UNKNOWN']),
  campaign: z.string().nullable(),
  landingPath: z.string().nullable(),
  declaredSource: z.enum(leadDeclaredSources).nullable(),
  declaredDetail: z.string().nullable(),
  receivedAt: z.string(),
  ruleVersion: z.string(),
})
export type LeadAttribution = z.infer<typeof leadAttributionSchema>

export interface SetLeadDeclaredSourceCommand {
  leadId: string
  declaredSource: LeadDeclaredSource | null
  declaredDetail?: string
  /** Seulement à la création manuelle : l'origine d'une ligne existante ne change jamais. */
  formOrigin?: 'MANUAL'
}
